// TemplatePerformance.jsx
// This component displays the performance metrics of WhatsApp message templates in a table format.
// It shows delivery, open, and bounce rates, and allows viewing detailed info in a modal.

import React, { useState, useCallback, useEffect } from "react";
import { Flex, Modal, Table, Typography } from "antd";
import { CheckOutlined, EyeOutlined } from "@ant-design/icons";
import TemplateInfoAndInsights from "../../../components/templates/newDesignes/TemplateInfoAndInsights";
import { getTemplatePerformance } from "../../../../../reduxToolkit/channelSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import moment from "moment";
import { useLocation, useSearchParams } from "react-router-dom";
import CalenderFilled from "../../../../../assets/customSVG/CalenderFilled";
import TrendingThreeStars from "../../../../../assets/customSVG/TrendingThreeStars";

const { Title, Text } = Typography;

/**
 * TemplatePerformance component
 *
 * Displays WhatsApp message template performance metrics in a table format.
 * Allows viewing detailed template information in a modal.
 */
const TemplatePerformance = ({ start_date, end_date }) => {
  // State to control modal visibility
  const [openModal, setOpenModal] = useState(false);
  // State to hold the currently selected template for detailed view
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const dispatch = useDispatch();
  // Get current brand info from global state
  const { currentBrand } = useSelector((state) => state.app);
  const brandId = currentBrand?.brand_id;
  // Get template performance data and filters from global state
  const {
    templatePerformanceList,
    templatePerformanceListCount, // (Unused, but fetched)
    templatePerformanceLoading,
    dateRangeFilter,
  } = useSelector((state) => state.Channel);

  // Destructure date range filter
  const [start, end] = dateRangeFilter;

  let startDate = start_date || start;
  let endDate = end_date || end;

  /**
   * Prepare table data by parsing metrics from each template's data
   *
   * @returns {Array} Table data with parsed metrics
   */
  const tableData = templatePerformanceList
    .map((item) => {
      const parsedData = JSON.parse(item.data); // Parse JSON string to object
      const metrics = Object.values(parsedData); // Get all metric objects
      // Aggregate metrics across all entries
      const totalSent = metrics.reduce(
        (sum, m) => sum + m.message_sent_count,
        0,
      );
      const totalDelivered = metrics.reduce(
        (sum, m) => sum + m.delivered_count,
        0,
      );
      const totalRead = metrics.reduce((sum, m) => sum + m.read_count, 0);
      const totalBounced = metrics.reduce((sum, m) => sum + m.bounced_count, 0);
      return {
        key: item.id,
        name: item.name,
        deliveryRate: `${((totalDelivered / totalSent) * 100 || 0).toFixed(
          2,
        )}%`, // % delivered
        openRate: `${((totalRead / totalDelivered) * 100 || 0).toFixed(2)}%`, // % opened (from delivered)
        bounceRate: `${((totalBounced / totalSent) * 100 || 0).toFixed(2)}%`, // % bounced
        createdAt: `Created @ ${moment(item.created_at).format(
          "MMM DD, hh:mma",
        )}`,
        rawData: parsedData, // For modal details
        templateObj: item, // Original template object
      };
    })
    .filter((item) => parseFloat(item.deliveryRate.replace("%", "")) > 0)
    .sort((a, b) => {
      const rateA = parseFloat(a.deliveryRate.replace("%", ""));
      const rateB = parseFloat(b.deliveryRate.replace("%", ""));
      return rateB - rateA;
    });

  /**
   * Define table columns with custom renderers for each metric
   *
   * @returns {Array} Table columns with custom renderers
   */
  const columns = [
    {
      title: <Text>Name</Text>,
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text, record, index) => (
        <Flex>
          <div className="flex flex-col gap-2">
            <Text>{text.charAt(0).toUpperCase() + text.slice(1)}</Text>
            <Flex gap={4} align="center">
              <div>
                <CalenderFilled />
              </div>

              <Text type="secondary" className="text-xs">
                {record.createdAt}
              </Text>
            </Flex>
          </div>

          {/* <div>
            {index === 0 && (
              <Flex
                className="rounded-full px-2 py-[2px]"
                style={{ background: "#FF3629" }}
                justify="center"
                align="center"
                gap={6}
              >
                <TrendingThreeStars />
                <Text className="text-white text-xs">Trending</Text>
              </Flex>
            )}
            {index === 1 && (
              <Flex
                className="rounded-full px-2 py-[2px]"
                style={{ background: "#FBBF24" }}
              >
                <Text className="text-white text-xs">Hot</Text>
              </Flex>
            )}
          </div> */}
        </Flex>
      ),
    },
    {
      title: <Text>Delivery rate</Text>,
      dataIndex: "deliveryRate",
      key: "deliveryRate",
      width: 150,
      align: "center",
      render: (text) => <Text className="text-[#1A1A1A]">{text}</Text>,
    },
    {
      title: <Text>Open rate</Text>,
      dataIndex: "openRate",
      key: "openRate",
      width: 150,
      align: "center",
      render: (text) => <Text className="text-[#1A1A1A]">{text}</Text>,
    },
    {
      title: <Text>Bounce rate</Text>,
      dataIndex: "bounceRate",
      key: "bounceRate",
      width: 150,
      align: "center",
      render: (text) => <Text className="text-[#1A1A1A]">{text}</Text>,
    },
    {
      title: <Text>Actions</Text>,
      key: "actions",
      width: 100,
      align: "center",
      render: (text, record) => (
        <EyeOutlined
          onClick={() => {
            setSelectedTemplate(record); // Set template for modal
            setOpenModal(true); // Open modal
          }}
          className="text-green-500"
        />
      ),
    },
  ];

  /**
   * Fetch template performance data whenever the date range changes
   */
  useEffect(() => {
    dispatch(
      getTemplatePerformance({
        brand_id: brandId,
        start_date: startDate,
        end_date: endDate,
      }),
    );
  }, [dispatch, dateRangeFilter]);

  return (
    <div className="flex flex-col w-full h-full">
      {/* Section title */}
      <Title level={5} className="mb-4 !text-[#1A1A1A]">
        Best Performing Templates
      </Title>
      <div className="flex-1 w-full p-4 bg-white rounded-lg transition-all duration-300 ease-in-out">
        {/* Modal for viewing template details */}
        <Modal
          styles={{
            content: { padding: 12, borderRadius: 12, background: "#F9F9FC" },
            header: { background: "#F9F9FC" },
          }}
          centered
          footer={null}
          open={openModal}
          onCancel={() => setOpenModal(false)}
          title="Template Information"
          destroyOnClose
          width="70vw"
        >
          <TemplateInfoAndInsights
            performanceData={selectedTemplate?.rawData}
            selectedTemplate={selectedTemplate?.templateObj}
          />
        </Modal>
        {/* Table showing all template performance metrics */}
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          bordered={false}
          showHeader={true}
          loading={templatePerformanceLoading}
          scroll={{
            y: 300,
            x: "max-content",
          }}
          // onScroll={handleTableScroll}
          className="custom-table w-full transition-all duration-300"
          style={{ minWidth: "100%" }}
        />
      </div>
    </div>
  );
};

export default TemplatePerformance;
